import { router, usePage } from "@inertiajs/react"
import { LogOut, Cloud, CloudOff, Settings } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"

interface SharedProps {
  currentUser: { id: number; name: string; email: string } | null
  dropboxConnected: boolean
}

export function UserMenu() {
  const { currentUser, dropboxConnected } = usePage<{ props: SharedProps }>().props as unknown as SharedProps

  if (!currentUser) return null

  const initials = currentUser.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)

  function handleLogout() {
    router.delete("/session")
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground"
          data-testid="user-menu-trigger"
        >
          {initials}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="text-sm font-medium" data-testid="user-name">{currentUser.name}</span>
            <span className="text-xs text-muted-foreground" data-testid="user-email">{currentUser.email}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="gap-2" disabled>
          {dropboxConnected ? (
            <>
              <Cloud className="h-4 w-4" />
              <span>Dropbox</span>
              <Badge variant="secondary" className="ml-auto text-xs" data-testid="dropbox-status">
                Connected
              </Badge>
            </>
          ) : (
            <>
              <CloudOff className="h-4 w-4" />
              <span>Dropbox</span>
              <Badge variant="outline" className="ml-auto text-xs" data-testid="dropbox-status">
                Not connected
              </Badge>
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => router.visit("/settings")} className="gap-2" data-testid="settings-link">
          <Settings className="h-4 w-4" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleLogout} className="gap-2" data-testid="logout-button">
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
